import {
  computed,
  inject,
  MaybeRefOrGetter,
  Ref,
  ref,
  ShallowRef,
  toValue,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { TrackingAddKeywordsData } from '@/api/tracking';
import { KEYWORD_COMPLETION_DEBOUNCE_TIME } from '@/constants';
import { CompletionItem } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useSearchHistories } from '@/utils/composables/useSearchHistories';
import { useKeyboardNavigation } from '@/utils/composables/useSearchSuggestionKeyboard';
import { DebounceRef } from '@/utils/debounce';
import { escapeRegExpSpecialCharacters } from '@/utils/regexp';
import { useKeywordCompletion } from '@/utils/swr';
import { userSession } from '@/utils/userSession';
import { useEmitter, useStore } from '@/utils/vue';

const SEARCH_ITEM_HEIGHT = 40;

export const dropdownHeight = (itemsLength: number) =>
  itemsLength * SEARCH_ITEM_HEIGHT;

// v-for の template ref なので、要素ではなく要素の配列を保持する
export type SuggestionItemRefs = Readonly<ShallowRef<HTMLElement[] | null>>;

export type SearchSuggestionsTemplateRefs = {
  historyRefs: SuggestionItemRefs;
  autocompleteRefs: SuggestionItemRefs;
};

export type SearchSuggestionsCallbacks = {
  onUpdateModelValue: (value: string) => void;
  onSearch: () => void;
  onDeleteHistory: () => void;
};

// NOTE: `useSearchHistory` を inject するため、provide 済みのツリー内で呼ぶ必要がある
export const useSearchSuggestions = (
  modelValue: MaybeRefOrGetter<string>,
  { historyRefs, autocompleteRefs }: SearchSuggestionsTemplateRefs,
  callbacks: SearchSuggestionsCallbacks,
) => {
  const emitter = useEmitter();
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const { getCurrentPathForTracking } = useCurrentPathForTracking();

  // 検索窓
  const inputValue = ref(toValue(modelValue));
  const originalValue = ref<string>();
  const handledValue = computed(() => originalValue.value ?? inputValue.value);

  // 検索履歴
  const useSearchHistory = inject('useSearchHistory') as Ref<boolean>;
  const isHistoryEnabled = ref(false);
  const isDisplayedHistories = computed(
    () => isHistoryEnabled.value && !shouldFetchNewHistories.value,
  );
  const hoveredIndex = ref(-1);
  const shouldFetchNewHistories = ref(false);
  const {
    fetchLatestSearchHistories,
    createSearchHistory,
    deleteSearchHistory,
    searchHistories,
  } = useSearchHistories(userSession.getUserId());

  // autocomplete
  const isAutocompleteEnabled = ref(false);
  const debouncedInput = DebounceRef(
    computed(() => ({ text: handledValue.value })),
    KEYWORD_COMPLETION_DEBOUNCE_TIME,
  );
  const { data: completionsData } = useKeywordCompletion(debouncedInput);
  const completions = computed<CompletionItem[]>(
    () =>
      completionsData.value?.candidates
        .filter(item => item.text !== handledValue.value)
        .map(item => {
          const splitter = new RegExp(
            '(' + escapeRegExpSpecialCharacters(handledValue.value) + ')',
            'i',
          );
          const words = item.text.split(splitter).filter(w => w.trim() !== '');
          return {
            keyword: item.text,
            splitKeywords: [words[0], words.slice(1).join('')],
            score: item.score,
          };
        })
        .sort((a, b) => b.score - a.score) ?? [],
  );

  watch(
    () => toValue(modelValue),
    async value => {
      inputValue.value = value;
      originalValue.value = value;
      // NOTE: 検索履歴の再取得は、検索が実行された後の1回のみに抑制する
      if (inputValue.value.trim() === '' && shouldFetchNewHistories.value) {
        await fetchLatestSearchHistories();
        shouldFetchNewHistories.value = false;
      }
    },
  );

  const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target) {
      inputValue.value = target.value;
      originalValue.value = target.value;
      callbacks.onUpdateModelValue(target.value);
      updateEditingState(target.value);
      resetIndex();
    }
  };

  const onFocus = () => {
    isHistoryEnabled.value = inputValue.value.trim() === '';
    isAutocompleteEnabled.value = inputValue.value.trim() !== '';
  };

  let isComposing = false;
  const onCompositionStart = () => {
    isHistoryEnabled.value = false;
    isComposing = true;
  };
  const onCompositionEnd = () => {
    isComposing = false;
  };

  const onBlur = (event: FocusEvent) => {
    // blurですぐに閉じてしまうとdropdown上のクリックイベントが拾えないので少しずらす
    setTimeout(() => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (
        !relatedTarget ||
        !relatedTarget.classList.contains('delete-button')
      ) {
        resetState();
        resetFocus();
        resetIndex();
      }
    }, 200);
  };

  const onKeyDown = async (event: KeyboardEvent) => {
    if (isComposing) return;

    if (isAutocompleteEnabled.value) {
      // キーワード補完の最後の要素は要約結果画面への動線で、インデックスを共有させるために+1している
      onAutocompleteKeyDown(
        event,
        completions.value.length + 1,
        selectAutocomplete,
      );
      if (
        autocompleteFocusIndex.value === -1 ||
        autocompleteFocusIndex.value === completions.value.length
      ) {
        inputValue.value = originalValue.value ?? '';
      } else {
        inputValue.value =
          completions.value[autocompleteFocusIndex.value].keyword;
        const autocomplete = autocompleteRefs.value?.find(
          b => Number(b.dataset.index) === autocompleteFocusIndex.value,
        );
        autocomplete?.scrollIntoView({
          block: 'nearest',
        });
      }
    } else if (isHistoryEnabled.value) {
      onHistoryKeyDown(event, searchHistories.value.length, selectHistory);
      if (historyFocusIndex.value === -1) {
        inputValue.value = originalValue.value ?? '';
      } else {
        inputValue.value = searchHistories.value[historyFocusIndex.value].query;
        const history = historyRefs.value?.find(
          b => Number(b.dataset.index) === historyFocusIndex.value,
        );
        history?.scrollIntoView({
          block: 'nearest',
        });
      }
    }
  };

  const handlers = {
    input: onInput,
    focus: onFocus,
    compositionstart: onCompositionStart,
    compositionend: onCompositionEnd,
    blur: onBlur,
    keydown: onKeyDown,
  };

  // キーボード操作のhooks
  const {
    focusIndex: historyFocusIndex,
    onKeyDown: onHistoryKeyDown,
    resetIndex: historyResetIndex,
  } = useKeyboardNavigation();
  const {
    focusIndex: autocompleteFocusIndex,
    onKeyDown: onAutocompleteKeyDown,
    resetIndex: autocompleteResetIndex,
  } = useKeyboardNavigation();

  const selectHistory = async (index: number) => {
    const value = searchHistories.value[index]?.query || inputValue.value;
    if (value.trim() === '') return;
    useSearchHistory.value = true;
    await search(value);
  };

  const selectAutocomplete = async (index: number) => {
    // キーワードサジェストの最後の要素は要約結果画面への動線
    if (index === completions.value.length) {
      resetFocus();
      resetState();
      resetIndex();
      shouldFetchNewHistories.value = true;
      startSurvey();
      return;
    }

    const value = completions.value[index]?.keyword || inputValue.value;
    if (value.trim() === '') return;
    await search(value);

    // tracking
    if (index >= 0) {
      const trackData: TrackingAddKeywordsData = {
        pageName: route.name ?? '',
        pageUrl: getCurrentPathForTracking(),
        keywords: [value],
        input_text: handledValue.value,
        themeId: route.params.themeId
          ? Number(route.params.themeId)
          : undefined,
        rank: index + 1, // ログは1はじまりのルール
      };
      await api.trackAddKeywordsEvent(trackData);
    }
  };

  const search = async (value: string) => {
    inputValue.value = value;
    callbacks.onUpdateModelValue(value);
    callbacks.onSearch();
    await createSearchHistory(value);
    shouldFetchNewHistories.value = true;
    resetState();
    resetFocus();
    resetIndex();
  };

  const deleteHistory = async (query: string) => {
    inputValue.value = '';
    resetFocus();
    resetIndex();
    await deleteSearchHistory(query);
    await fetchLatestSearchHistories();
    callbacks.onDeleteHistory();
  };

  const isDisplayedHistoryList = computed(
    () => isDisplayedHistories.value && searchHistories.value.length > 0,
  );
  const isDropdownOpen = computed(
    () => isDisplayedHistoryList.value || isAutocompleteEnabled.value,
  );

  const resetFocus = () => (originalValue.value = inputValue.value);

  const resetIndex = () => {
    historyResetIndex();
    autocompleteResetIndex();
  };

  const updateEditingState = (value: string) => {
    isHistoryEnabled.value = value.trim() === '';
    isAutocompleteEnabled.value = value.trim() !== '';
  };

  const resetState = () => {
    isHistoryEnabled.value = false;
    isAutocompleteEnabled.value = false;
  };

  const startSurvey = async () => {
    if (!handledValue.value) return;

    const surveySession = await api.createSurveySession(handledValue.value);
    const surveySessionId = surveySession.session_id;

    await store.dispatch('survey/setSessionAllState', {
      sessionId: surveySessionId,
      question: handledValue.value,
      filterInfo: {
        searchScope: 'all',
        docTypes: [],
        specifiedFiles: [],
      },
      deepMode: false,
      specifiedMethod: undefined,
      executionFrom: 'search_bar',
      surveyType: 'survey',
    });

    router.push({
      name: 'surveySession',
      params: { survey_session_id: surveySessionId },
    });

    // サイドメニューの履歴に反映させるため通知
    emitter.emit('update-survey-session', {
      session_id: surveySessionId,
      title: handledValue.value,
      updated_at: new Date().toISOString(),
    });
  };

  return {
    inputValue,
    handledValue,
    handlers,
    onKeyDown,
    search,
    searchHistories,
    historyFocusIndex,
    hoveredIndex,
    isDisplayedHistories,
    isDisplayedHistoryList,
    selectHistory,
    deleteHistory,
    completions,
    autocompleteFocusIndex,
    isAutocompleteEnabled,
    selectAutocomplete,
    isDropdownOpen,
  };
};
